import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Tables, TablesInsert } from "@/integrations/supabase/types";
import { toast } from "sonner";

export type Deal = Tables<"deals">;
export type DealInsert = TablesInsert<"deals">;
export type PipelineStage = Tables<"pipeline_stages">;
export type Pipeline = Tables<"pipelines">;

export function useDeals() {
  return useQuery({
    queryKey: ["deals"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("deals")
        .select(`
          *,
          accounts(name),
          contacts:primary_contact_id(first_name, last_name),
          pipeline_stages(name, color, position)
        `)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
  });
}

export function usePipelines() {
  return useQuery({
    queryKey: ["pipelines"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("pipelines")
        .select("*")
        .order("created_at", { ascending: true });

      if (error) throw error;
      return data as Pipeline[];
    },
  });
}

export function usePipelineStages(pipelineId?: string) {
  return useQuery({
    queryKey: ["pipeline_stages", pipelineId],
    queryFn: async () => {
      let query = supabase
        .from("pipeline_stages")
        .select("*")
        .order("position", { ascending: true });

      if (pipelineId) {
        query = query.eq("pipeline_id", pipelineId);
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as PipelineStage[];
    },
    enabled: !!pipelineId || pipelineId === undefined,
  });
}

export function useCreateDeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (deal: Omit<DealInsert, "tenant_id">) => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from("profiles")
        .select("tenant_id")
        .eq("id", user.id)
        .maybeSingle();

      if (!profile?.tenant_id) {
        throw new Error("No tenant assigned. Please contact support.");
      }

      const { data, error } = await supabase
        .from("deals")
        .insert({ ...deal, tenant_id: profile.tenant_id })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deals"] });
      toast.success("Deal created successfully");
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}

export function useUpdateDeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, previousStageName, ...updates }: Partial<Deal> & { id: string; previousStageName?: string }) => {
      const { data, error } = await supabase
        .from("deals")
        .update(updates)
        .eq("id", id)
        .select(`
          *,
          pipeline_stages(name)
        `)
        .single();

      if (error) throw error;

      // If stage changed, trigger notification
      if (updates.stage_id && previousStageName) {
        const newStageName = (data as any).pipeline_stages?.name;
        if (newStageName && newStageName !== previousStageName) {
          try {
            await supabase.functions.invoke("notify-deal-stage", {
              body: {
                dealId: id,
                previousStage: previousStageName,
                newStage: newStageName,
                dealTitle: data.title,
              },
            });
          } catch (notifyError) {
            console.error("Failed to send deal stage notification:", notifyError);
          }
        }
      }

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["deals"] });
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });
}

export function useScoreDeal() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (deal: {
      id: string;
      title: string;
      amount: number | null;
      stage_name: string;
      stage_probability: number;
      days_in_stage: number;
      account_name?: string;
      contact_name?: string;
      has_proposal: boolean;
      call_count: number;
      last_activity_days: number;
    }) => {
      const { data, error } = await supabase.functions.invoke("score-deal", {
        body: { deal },
      });

      if (error) throw error;

      // Update the deal with the AI score
      const { error: updateError } = await supabase
        .from("deals")
        .update({ ai_win_probability: data.win_probability })
        .eq("id", deal.id);

      if (updateError) throw updateError;

      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["deals"] });
      toast.success(`Deal scored: ${data.win_probability}% win probability`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to score deal: ${error.message}`);
    },
  });
}
